import React, { ReactElement, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import ModalContainer from 'components/molecules/ModalContainer';
import { FormControl, Grid, Button, TextField, CircularProgress } from '@material-ui/core';
import useWarehouse from 'hooks/useWarehouse';
import usePackage from 'hooks/usePackage';
import useCities from 'hooks/useCities';
import Autocomplete from '@material-ui/lab/Autocomplete';
import useShipping from 'hooks/useShippingCarrier';
import { downloadPDF } from 'utils/downloadPDF';
import useCustomSnackbar from 'hooks/useCustomSnackbar';
import { useOrdersContext } from 'contextApi/OrdersContext';
import { removeAccents } from 'utils/validator';
import useUser from 'hooks/useUser';
import OrderPackageList from 'components/organisms/OrderPackageList';
import { ICity } from 'interfaces/city.interface';
import { IShipping, IShippingRequest } from 'interfaces/shipping.interface';
import { IPackage } from 'interfaces/shippingPackage.interface';
import { IWarehouse } from 'interfaces/warehouse.interface';
import { IOrderShippingRequest, IPackages } from 'interfaces/orderPackages.interface';

const defaultCity: ICity = {
  codigo: '',
  nombre: 'Selecciona la ciudad destino',
  nombre_departamento: ''
};

interface Props {
  getOrders(): void;
}
export default function CreateShippingModal({ getOrders }: Props): ReactElement {
  const {
    openModalCreateShipping,
    orderToShipping,
    orderPackageShipping,
    orderPackages,
    setOrderPackageShipping,
    handleOpenModalCreateShipping
  } = useOrdersContext();
  const { user } = useUser();

  const { handleSubmit, register, errors, control, watch, reset } = useForm<IShipping>();
  const [formDirty, setFormDirty] = useState(false);
  const [departamentos, setDepartamentos] = useState<string[]>([]);
  const [departamento, setDepartamento] = useState<string | null>('');
  const [city, setCity] = useState<ICity>(defaultCity);
  const [isLoading, setIsLoading] = useState(false);
  const [cities, setCities] = useState<ICity[]>([]);
  const [allCities, setAllCities] = useState<ICity[]>([]);
  const [packages, setPackages] = useState<IPackage[]>([]);
  const [warehouses, setWarehouses] = useState<IWarehouse[]>([]);

  const watchLabelId = watch('labelId');
  const watchwarehouseId = watch('warehouseId');
  const shippingType = watch('shippingType');
  const deliveryData = watch('delivery');

  const { getCities } = useCities();
  const { getWarehouses } = useWarehouse();
  const { getPackages } = usePackage();
  const { createTrackingId, createTrackingDelivery } = useShipping();
  const { showNotistack } = useCustomSnackbar();

  useEffect(() => {
    setOrderPackageShipping({
      ...orderPackageShipping,
      labelId: watchLabelId,
      warehouseId: watchwarehouseId,
      delivery: deliveryData,
      cityCode: city.codigo
    });
  }, [watchLabelId, watchwarehouseId, shippingType, deliveryData]);

  // Optimizar el useEffect de arriba
  useEffect(() => {
    setIsLoading(true);
    setDepartamento('');
    setCities([]);
    if (warehouses.length === 0) {
      getWarehouses().then((result) => {
        setWarehouses(result);
      });
    }
    if (packages.length === 0) {
      getPackages()
        .then((result) => {
          setPackages(result);
        })
        .catch(() => {
          setPackages([]);
        });
    }

    getCities()
      .then((result) => {
        setAllCities(result);
        const departamentsUniques: string[] = [];
        result
          .map((city) => city.nombre_departamento)
          .filter((dep) => {
            if (departamentsUniques.indexOf(dep) === -1) {
              departamentsUniques.push(dep);
            }
          });
        setDepartamentos(departamentsUniques);
        const cityToAssign = result.find(
          (cityToFilter) =>
            removeAccents(cityToFilter.nombre?.trim().toLowerCase()) ===
            removeAccents(orderToShipping.recipientsCityName?.trim().toLowerCase())
        );

        // eslint-disable-next-line no-console
        console.log(`Ciudad del pedido "${orderToShipping.recipientsCityName}"`);

        if (cityToAssign) {
          // eslint-disable-next-line no-console
          console.log('Se selecciona la ciudad del pedido', cityToAssign);
          setCity(cityToAssign);
          setDepartamento(cityToAssign.nombre_departamento.trim());
          setCities(
            allCities.filter(
              (cityToFilter) => cityToFilter.nombre_departamento.trim() === cityToAssign.nombre_departamento.trim()
            )
          );
        } else {
          // eslint-disable-next-line no-console
          console.log('Se selecciona la ciudad por defecto automáticamente', defaultCity);
          setCity(defaultCity);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    reset({
      customerName: orderToShipping.recipientsName.trim(),
      reference: orderToShipping.name,
      recipientsAddress: orderToShipping.recipientsAddress,
      shippingType: user?.userName !== 'dco' ? '2' : undefined
    });
  }, [orderToShipping]);

  const onSubmit = (data: IShipping) => {
    setFormDirty(true);
    if (!city.codigo) {
      return;
    } else {
      data.cityCode = city.codigo;
    }

    let dataToSend: IShippingRequest | IOrderShippingRequest = {};

    if (data.packageId) {
      dataToSend = {
        cityCode: data.cityCode,
        labelId: data.labelId,
        orderId: orderToShipping.orderNumber,
        packageId: data.packageId,
        warehouseId: data.warehouseId,
        delivery: data.delivery,
        serviceLevel: Number(data.serviceLevel)
      };
    } else {
      if (orderPackages.packages) {
        const isStatusPackages: IPackages[] = orderPackages.packages.filter(
          (packageDetails) => packageDetails.status === false
        );

        if (isStatusPackages.length > 0) {
          dataToSend = {
            cityCode: data.cityCode,
            labelId: data.labelId,
            orderId: orderToShipping.orderNumber,
            packages: isStatusPackages,
            warehouseId: data.warehouseId,
            delivery: data.delivery,
            serviceLevel: Number(data.serviceLevel)
          };
        } else {
          getOrders();
          showNotistack({ message: 'Los paquetes ya se encuentran enviados' });
          handleOpenModalCreateShipping(false);
          return;
        }
      }
    }

    if (shippingType === '2') {
      createTrackingId(dataToSend)
        .then((response) => {
          downloadPDF(response.pdf, `pedido_${orderToShipping.orderNumber}_guia_${response.trackingId}`);
          showNotistack({ message: 'Guía generada correctamente' });
          getOrders();
          handleOpenModalCreateShipping(false);
        })
        .catch((err) => {
          if (err.response?.status === 400 && err.response.data?.message) {
            showNotistack({ message: err.response.data?.message });
          } else {
            showNotistack({
              message: typeof err === 'string' ? err : 'Ocurrió un error, contacta con soporte por favor.'
            });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      createTrackingDelivery(dataToSend)
        .then((response) => {
          downloadPDF(response.pdf, `pedido_${orderToShipping.orderNumber}_guia_${response.trackingId}`);
          showNotistack({ message: 'Guía generada correctamente' });
          getOrders();
          handleOpenModalCreateShipping(false);
        })
        .catch((err) => {
          if (err.response?.status === 400 && err.response.data?.message) {
            showNotistack({ message: err.response.data?.message });
          } else {
            showNotistack({
              message: typeof err === 'string' ? err : 'Ocurrió un error, contacta con soporte por favor.'
            });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    setIsLoading(true);
  };

  return (
    <ModalContainer
      open={openModalCreateShipping}
      onClose={() => {
        setCity(defaultCity);
        handleOpenModalCreateShipping(false);
      }}
      title="Generar guía"
      maxWidth="lg"
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {/* customerName */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Cliente"
              // value={orderToShipping.recipientsName}
              InputProps={{
                readOnly: true
              }}
              variant="outlined"
              inputRef={register<HTMLInputElement>({ required: 'El nombre del destinatario es requerido.' })}
              name="customerName"
              error={errors.customerName ? true : false}
              helperText={errors.customerName?.message}
            />
          </Grid>
          {/* name order number with prefix */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Pedido"
              value={orderToShipping.name}
              InputProps={{
                readOnly: true
              }}
              variant="outlined"
            />
          </Grid>
          {/* departamento */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              value={departamento}
              fullWidth
              // getOptionLabel={(option) => option.name}
              onChange={(_, value: string | null) => {
                setDepartamento(value);
                const citiesByDepartamento = allCities.filter((city) => city.nombre_departamento === value);
                setCities(citiesByDepartamento);
                setCity(defaultCity);
                setOrderPackageShipping({ ...orderPackageShipping, cityCode: city.codigo });
              }}
              id="departamento"
              disabled
              options={departamentos}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Departamento destino"
                  disabled
                  error={formDirty && !departamento}
                  helperText={formDirty && !departamento ? 'Este campo es requerido' : ''}
                  variant="outlined"
                />
              )}
            />
          </Grid>
          {/* city */}
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              value={city}
              fullWidth
              disabled
              getOptionLabel={(option) => option.nombre}
              onChange={(event: any, newCity: ICity | null) => {
                if (newCity) {
                  setCity(newCity);
                  setOrderPackageShipping({ ...orderPackageShipping, cityCode: newCity.codigo });
                }
              }}
              id="city"
              options={cities}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Ciudad destino"
                  disabled
                  error={formDirty && !city.codigo}
                  helperText={formDirty && !city.codigo ? 'Este campo es requerido' : ''}
                  variant="outlined"
                />
              )}
            />
          </Grid>
          {/* recipientsAddress */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Dirección de envío"
              // value={orderToShipping.recipientsName}
              InputProps={{
                readOnly: true
              }}
              variant="outlined"
              inputRef={register<HTMLInputElement>({ required: 'Dirección del destinatario es requerido.' })}
              name="recipientsAddress"
              error={errors.recipientsAddress ? true : false}
              helperText={errors.recipientsAddress?.message}
            />
          </Grid>
          {/* label id */}
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined">
              <Controller
                name="labelId"
                control={control}
                defaultValue="55"
                rules={{
                  required: 'Este campo es requerido'
                }}
                as={
                  <TextField
                    fullWidth
                    select
                    variant="outlined"
                    label="Formato del rótulo"
                    SelectProps={{
                      native: true
                    }}
                    error={errors.labelId ? true : false}
                    helperText={errors.labelId?.message}
                    InputLabelProps={{ shrink: true }}
                  >
                    <option aria-label="None" value="">
                      Selecciona formato del rótulo
                    </option>
                    <option aria-label="44" value="44">
                      Genérico
                    </option>
                    <option aria-label="55" value="55">
                      Genérico 10 x 10
                    </option>
                    <option aria-label="57" value="57">
                      Genérico tercio de página
                    </option>
                    <option aria-label="58" value="58">
                      Genérico media página
                    </option>
                    <option aria-label="59" value="59">
                      Genérico cuarto de página
                    </option>
                  </TextField>
                }
              />
            </FormControl>
          </Grid>
          {/* warehouseId */}
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined">
              <Controller
                name="warehouseId"
                control={control}
                rules={{
                  required: 'Este campo es requerido'
                }}
                as={
                  <TextField
                    fullWidth
                    select
                    variant="outlined"
                    label="Bodega origen"
                    SelectProps={{
                      native: true
                    }}
                    error={errors.warehouseId ? true : false}
                    helperText={errors.warehouseId?.message}
                    InputLabelProps={{ shrink: true }}
                  >
                    <option aria-label="None" value="">
                      Selecciona bodega origen
                    </option>
                    {warehouses.map((warehouse) => (
                      <option value={warehouse.id} key={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </TextField>
                }
              />
            </FormControl>
          </Grid>
          {/* Products */}
          {user?.userName === 'dco' ? (
            <React.Fragment>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined">
                  <Controller
                    name="shippingType"
                    control={control}
                    rules={{
                      required: 'Este campo es requerido'
                    }}
                    as={
                      <TextField
                        fullWidth
                        select
                        variant="outlined"
                        label="Enviar por"
                        SelectProps={{
                          native: true
                        }}
                        error={errors.shippingType ? true : false}
                        helperText={errors.shippingType?.message}
                        InputLabelProps={{ shrink: true }}
                      >
                        <option aria-label="None" value="">
                          Selecciona uno
                        </option>
                        <option value={1} key={1}>
                          Mensajero
                        </option>
                        <option value={2} key={2}>
                          Coordinadora
                        </option>
                      </TextField>
                    }
                  />
                </FormControl>
              </Grid>
              {shippingType === '1' && (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    required
                    name="delivery"
                    variant="outlined"
                    fullWidth
                    inputRef={register<HTMLInputElement>({
                      required: 'Este campo es requerido'
                    })}
                    defaultValue={'Jorge Cruz'}
                    label="Nombre del mensajero"
                    disabled={isLoading}
                    error={errors.delivery ? true : false}
                    helperText={errors.delivery && errors.delivery.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
            </React.Fragment>
          ) : null}
          <OrderPackageList shippingType={shippingType} dropshipper={user?.userName} />
          <Grid container justify="center" spacing={2}>
            <Button
              type="submit"
              disabled={isLoading}
              variant="contained"
              color="primary"
              style={{ marginTop: 20 }}
              onClick={() => {
                setFormDirty(true);
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : ''} Enviar todo
            </Button>
            <Button
              variant="contained"
              color="default"
              disabled={isLoading}
              style={{ marginTop: 20, marginLeft: 20 }}
              onClick={() => {
                getOrders();
                setCity(defaultCity);
                handleOpenModalCreateShipping(false);
              }}
            >
              Cerrar
            </Button>
          </Grid>
        </Grid>
      </form>
    </ModalContainer>
  );
}
